import type { ReactNode } from "react";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Icon, type IconType, type IconVariant } from "@jsq/ds/icons";
import type { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";

type ConfirmDialogAction =
  | { kind: "single"; label: string; testId?: string }
  | {
      kind: "confirm";
      confirmLabel: string;
      confirmVariant: "danger" | "primary";
      cancelLabel: string;
      onConfirm: () => void;
      isConfirming?: boolean;
      confirmTestId?: string;
    };

export type ConfirmDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Used for screen readers (the title is visually hidden). */
  title: string;
  heading: ReactNode;
  description: ReactNode;
  icon: { type: IconType; color: Colors; variant?: IconVariant };
  action: ConfirmDialogAction;
  /** `data-testid` for the modal root. */
  testId?: string;
};

export const ConfirmDialog = ({
  isOpen,
  onClose,
  title,
  heading,
  description,
  icon,
  action,
  testId,
}: ConfirmDialogProps) => (
  <ModalV2
    show={isOpen}
    onHide={onClose}
    title={title}
    size="small"
    visuallyHideTitle
    data-testid={testId}
  >
    <ModalV2.Body>
      <Flex flexDirection="column" gap={SPACING.MEDIUM}>
        <Flex flexDirection="column" alignItems="center" gap={SPACING.MEDIUM}>
          <Icon type={icon.type} size="xxx-large" color={icon.color} variant={icon.variant} />
          <Text variant="h2">{heading}</Text>
          <Text variant="secondary-md" align="center">
            {description}
          </Text>
        </Flex>
        <Flex flexDirection="column" gap={SPACING.SMALL}>
          {action.kind === "single" ? (
            <Button variant="neutral" onClick={onClose} isFullWidth data-testid={action.testId}>
              {action.label}
            </Button>
          ) : (
            <>
              <Button
                variant={action.confirmVariant}
                onClick={action.onConfirm}
                isLoading={action.isConfirming}
                disabled={action.isConfirming}
                isFullWidth
                data-testid={action.confirmTestId}
              >
                {action.confirmLabel}
              </Button>
              <Button
                variant="neutral"
                onClick={onClose}
                disabled={action.isConfirming}
                isFullWidth
              >
                {action.cancelLabel}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </ModalV2.Body>
  </ModalV2>
);
